import FormInput from '@form/FormInput';
import { useApi } from '@hooks/useApi';
import { Selection } from 'components/DataTable';
import { FilterState } from 'components/DataTable/DataTable';
import { Form } from 'components/Form/base/Form';
import { FormSubmit } from 'components/Form/next';
import { messageValidationSchema } from 'components/MessageManagement/MessageComponent';
import { MessageFields } from 'components/MessageManagement/MessageComponent/MessageFields';
import Joi from 'joi';
import { omit } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

interface Props {
    filter?: FilterState;
    clubSlug: string;
    members: Selection;
    callback?: () => void;
}

export const MessageComponent = ({ clubSlug, filter, members, callback }: Props) => {
    const { t } = useTranslation('message-management');
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const { data } = useSWR(clubSlug ? `club-member/${clubSlug}/property` : null);

    const placeholders = useMemo(
        () => [
            { name: 'firstname', title: t('memberPlaceholders.firstname') },
            { name: 'lastname', title: t('memberPlaceholders.lastname') },
            { name: 'gender', title: t('memberPlaceholders.gender') },
            { name: 'email', title: t('memberPlaceholders.email') },
            { name: 'number', title: t('memberPlaceholders.number') },
            { name: 'debtor', title: t('memberPlaceholders.debtorNumber') },
            { name: 'address', title: t('memberPlaceholders.address') },
            { name: 'group', title: t('memberPlaceholders.group') },
            { name: 'category', title: t('memberPlaceholders.category') },
            { name: 'remarks', title: t('memberPlaceholders.remarks') },
            { name: 'entry_date', title: t('memberPlaceholders.entryDate') },
            { name: 'exit_date', title: t('memberPlaceholders.exitDate') },
            { name: 'ads', title: t('memberPlaceholders.ads') },
            { name: 'birthday', title: t('memberPlaceholders.birthday') },
            { name: 'phone', title: t('memberPlaceholders.phone') },
            { name: 'created_at', title: t('memberPlaceholders.createdAt') },
            ...(data?.data.flatMap(({ key, title, type }) => {
                const phs = [{ name: key, title }];

                if (type === 'iban') {
                    phs.push({ name: `${key}_disguise`, title: t('memberPlaceholders.disguised', { title }) });
                }

                return phs;
            }) || [])
        ],
        [data, t]
    );

    const emailProperties = useMemo(
        () => [
            { key: 'email', label: t('memberPlaceholders.memberEmail') },
            ...(data?.data?.map(({ key, title }) => ({
                key,
                label: title
            })) || [])
        ],
        [data, t]
    );

    const handleSubmit = (data) => {
        setIsLoading(true);
        return api({
            url: `club-member/${clubSlug}/message`,
            method: 'POST',
            data: { ...data, members, filter },
            onBefore: () => setIsLoading(false),
            onSuccess: () => callback?.()
        });
    };

    return (
        <Form
            onSubmit={handleSubmit}
            defaultValues={{
                email_property: 'email'
            }}
            validationSchema={Joi.object(
                omit(messageValidationSchema, ['addresses', 'addresses_cc', 'addresses_bcc'])
            ).unknown()}
        >
            <FormInput.Select
                name="email_property"
                label={t('memberMessage.emailPropertyLabel')}
                options={emailProperties}
            />
            <MessageFields withoutAddresses={['to']} placeholders={placeholders} />
            <FormInput.Checkbox
                name="send_to_all_members"
                label={t('memberMessage.sendToAllLabel')}
            />
            <FormSubmit isLoading={isLoading}>{t('common.submit')}</FormSubmit>
        </Form>
    );
};
